import {
  ImageIntentContext,
  isSaveBlockingEnabled,
  reportImageIntent,
} from '../../../../utils/imageIntentReporter';
import { IMAGE_INTENT_ATTRIBUTE } from './ImageIntentTouchStyle';

// How long a still finger must stay down to count as a long-press.
//
// Measured in Mobile Safari (iOS 18): when its own long-press save sheet is
// allowed to open, Safari cancels the page's touch (touchcancel) at the moment
// the sheet appears — 734–999 ms after touchstart. With the sheet suppressed
// (ImageIntentTouchStyle) the touch ends normally. Either way this timer fires
// first, so the attempt is logged whether or not the sheet is blocked.
export const LONG_PRESS_MS = 600;

// A touchcancel after the finger has been still this long is the OS claiming
// the long-press for itself (save sheet, drag lift), so it is logged as one. An
// earlier cancel — a scroll the browser took over, a system interruption — is
// not a long-press.
export const OS_LONG_PRESS_CLAIM_MS = 450;

// How far a finger may wander and still be holding still. A real finger is
// never perfectly still: Mobile Safari and Chrome Android both send touchmove
// during a long-press. This matches UIKit's long-press allowableMovement
// (10 pt) and exceeds Android's 8 dp touch slop, so a wander the OS still
// treats as a hold is a hold here too; a scroll or swipe passes it in its
// first frame. Measured in the iOS 18 simulator: Safari still lifted the image
// after 5.7 pt of wander and gave up at 11.3 pt.
export const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

export interface TouchPoint {
  x: number;
  y: number;
}

// The first finger's position, and how many fingers are down, from a touch
// event (React's or the DOM's).
export const touchPointOf = (event: {
  touches: ArrayLike<{ clientX: number; clientY: number }>;
}): TouchPoint | undefined => {
  const touch = event.touches[0];
  return touch ? { x: touch.clientX, y: touch.clientY } : undefined;
};

// Which kind of pointer opened a context menu. Chromium dispatches contextmenu
// as a PointerEvent, so a finger's long-press says 'touch'; a browser that
// sends a plain MouseEvent says nothing.
export const pointerTypeOf = (event: Event): string | undefined =>
  'pointerType' in event && typeof event.pointerType === 'string'
    ? event.pointerType
    : undefined;

// A phone or tablet whose primary input is a finger.
export const isCoarsePointer = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

interface Cancelable {
  preventDefault: () => void;
}

export interface ImageIntentGestureTracker {
  contextMenu: (
    event: Cancelable,
    context: ImageIntentContext,
    pointerType?: string,
  ) => void;
  dragStart: (event: Cancelable, context: ImageIntentContext) => void;
  touchStart: (
    context: ImageIntentContext,
    point?: TouchPoint,
    touchCount?: number,
  ) => void;
  touchMove: (point?: TouchPoint, touchCount?: number) => void;
  touchEnd: () => void;
  touchCancel: (context: ImageIntentContext) => void;
  dispose: () => void;
}

// Logs every save gesture on a preview image and blocks the ones that save:
//   - contextmenu: always blocked (preview images blocked right-click before
//     this feature, so the studio's save-blocking switch does not touch it).
//     From a finger it is that touch's long-press, and is logged as one rather
//     than as a right-click: Android Chrome opens its image menu while the
//     finger is still down, and Chromium on a Windows touchscreen opens it
//     only once the finger lifts (after touchend), with pointerType 'touch'.
//   - dragstart: blocked when it comes from a finger (drag to Photos/Files)
//     and the studio's save blocking is on. Mobile Safari fires dragstart
//     while the touch is still down, so an active touch identifies it even on
//     an iPad whose primary pointer is a trackpad. A finger can lift an image
//     only after holding it, and Safari lifts it 0.4–1 s into any still hold,
//     before its save sheet opens, so a finger drag is logged as that touch's
//     long-press: one label for one gesture, whichever of the lift and the
//     timer comes first (which depends on the studio's blocking switch). A
//     mouse or trackpad drag is logged as a drag and allowed.
//   - long-press: logged here, whichever of the timer, a finger dragstart, a
//     late touchcancel or a finger's contextmenu sees it first; the iOS save
//     sheet itself can only be stopped by CSS (ImageIntentTouchStyle). A
//     finger that wanders less than LONG_PRESS_MOVE_TOLERANCE_PX is still
//     holding; a second finger (a pinch) is never a long-press. The touch
//     handlers never preventDefault, so scrolling, pinch-zoom and carousel
//     swipes are untouched.
// One touch is one attempt: once it has reported, it reports nothing else.
// Mobile Safari fires dragstart during a long-press, so otherwise one iOS
// long-press would be logged twice.
// Blocking never hides an attempt: each handler reports before it cancels, and
// what a gesture blocks does not depend on whether it was reported.
//
// Measured in Mobile Safari (iOS 18) — what each gesture sends:
//   long-press, sheet allowed   touchstart, dragstart +525, touchcancel +1035
//   long-press, sheet blocked   touchstart, dragstart +652, touchend +1274
//   hold, then drag (allowed)   touchstart, dragstart +418, touchcancel +762
//   wandering 1-2 pt hold       touchmoves, then the same lift and sheet
//
// Copy is deliberately not tracked. A browser sends the copy event to the
// element where the text selection starts, and a selection cannot start inside
// an <img> (most previews also sit in user-select: none containers, and
// ImageIntentTouchStyle turns selection off on touch), so a listener here would
// never fire. The real copy actions are already counted: right-click -> Copy
// Image is blocked and logged as contextmenu; the iOS sheet's "Copy" and
// Android's "Copy image" are logged as longpress.
export const createImageIntentGestureTracker =
  (): ImageIntentGestureTracker => {
    let longPressTimer: ReturnType<typeof setTimeout> | null = null;
    let touchStartedAt: number | null = null;
    let touchOrigin: TouchPoint | null = null;
    let touchActive = false;
    let touchReported = false;

    const clearLongPress = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    const reportLongPress = (context: ImageIntentContext) => {
      if (touchReported) return;
      touchReported = true;
      reportImageIntent('longpress', context);
    };

    return {
      contextMenu: (event, context, pointerType) => {
        if (touchActive || pointerType === 'touch') {
          clearLongPress();
          reportLongPress(context);
          // A finger's menu after touchend is the last event of the touch
          // that just ended: reported only if that touch was not. Whatever
          // comes next is a new attempt, even from a browser that sends the
          // page no touch events (so no touchstart to start one).
          if (!touchActive) touchReported = false;
        } else {
          reportImageIntent('contextmenu', context);
        }
        event.preventDefault();
      },
      dragStart: (event, context) => {
        if (!touchActive) {
          reportImageIntent('dragstart', context);
        } else {
          clearLongPress();
          reportLongPress(context);
        }
        if (isSaveBlockingEnabled() && (touchActive || isCoarsePointer())) {
          event.preventDefault();
        }
      },
      touchStart: (context, point, touchCount = 1) => {
        // A second finger: a pinch, not a long-press. The touch stays active
        // (a drag or menu during it is still a finger's), but nothing more is
        // timed.
        if (touchActive && touchCount > 1) {
          clearLongPress();
          return;
        }
        clearLongPress();
        touchActive = true;
        touchOrigin = point ?? null;
        touchReported = false;
        touchStartedAt = Date.now();
        // The browser may deliver the first touchstart with both fingers
        // already down. Keep it active, but do not time a pinch as a hold.
        if (touchCount > 1) return;
        longPressTimer = setTimeout(() => {
          longPressTimer = null;
          reportLongPress(context);
        }, LONG_PRESS_MS);
      },
      touchMove: (point, touchCount = 1) => {
        if (!longPressTimer) return;
        if (touchCount > 1 || !point || !touchOrigin) {
          clearLongPress();
          return;
        }
        const dx = point.x - touchOrigin.x;
        const dy = point.y - touchOrigin.y;
        if (dx * dx + dy * dy > LONG_PRESS_MOVE_TOLERANCE_PX ** 2) {
          clearLongPress();
        }
      },
      touchEnd: () => {
        touchActive = false;
        clearLongPress();
      },
      touchCancel: (context) => {
        const stillPending = longPressTimer !== null;
        const heldMs =
          touchStartedAt === null ? 0 : Date.now() - touchStartedAt;
        touchActive = false;
        clearLongPress();
        if (stillPending && heldMs >= OS_LONG_PRESS_CLAIM_MS) {
          reportLongPress(context);
        }
      },
      dispose: () => {
        touchActive = false;
        clearLongPress();
      },
    };
  };

// The same logging and blocking for a preview image rendered outside React
// (the SweetAlert confirm dialogs), as native listeners. Returns a cleanup.
export const attachImageIntentListeners = (
  element: Element,
  context: ImageIntentContext,
): (() => void) => {
  const tracker = createImageIntentGestureTracker();
  element.setAttribute(IMAGE_INTENT_ATTRIBUTE, '');

  const listeners: Array<[string, (event: Event) => void]> = [
    [
      'contextmenu',
      (event) => tracker.contextMenu(event, context, pointerTypeOf(event)),
    ],
    ['dragstart', (event) => tracker.dragStart(event, context)],
    [
      'touchstart',
      (event) => {
        const touchEvent = event as TouchEvent;
        tracker.touchStart(
          context,
          touchPointOf(touchEvent),
          touchEvent.touches.length,
        );
      },
    ],
    [
      'touchmove',
      (event) => {
        const touchEvent = event as TouchEvent;
        tracker.touchMove(touchPointOf(touchEvent), touchEvent.touches.length);
      },
    ],
    ['touchend', () => tracker.touchEnd()],
    ['touchcancel', () => tracker.touchCancel(context)],
  ];
  listeners.forEach(([type, listener]) =>
    element.addEventListener(type, listener, {
      passive: type !== 'contextmenu' && type !== 'dragstart',
    }),
  );

  return () => {
    listeners.forEach(([type, listener]) =>
      element.removeEventListener(type, listener),
    );
    tracker.dispose();
  };
};
